import { BiDashboardHostContext } from '@euler/app/components/message/widgets/bi-dashboard-changes/BiDashboardHostContext';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useAlert } from '@euler/functions/ux/useAlert';
import { BiAnswerAction } from '@euler/model/bi/answer';
import {
  BiCardView,
  BiDashboardPermission,
  BiDashboardVisibility,
} from '@euler/model/bi/dashboard';
import { BiQueryResult } from '@euler/model/bi/result';
import { useCreation, useWatchValue } from '@euler/utils/hooks';
import { Lock, Pin, RefreshCw, Sparkles, Users } from 'lucide-react-native';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, View, useWindowDimensions } from 'react-native';
import { BiButton } from '../components/ui/BiButton';
import { BiPageHeader } from '../components/ui/BiPageHeader';
import { BiRetry } from '../components/ui/BiRetry';
import { BiTag } from '../components/ui/BiTag';
import { biCanEdit } from '../functions/biCanEdit';
import { kBiVisibilityTags } from '../functions/biDashboardText';
import { DashboardActions } from './DashboardActions';
import { DashboardContent } from './DashboardContent';
import { BiDashboardPanel, DashboardPanels } from './DashboardPanels';
import { DashboardChatPanel } from './chat/DashboardChatPanel';
import { kBiDashboardChatTool } from './chat/biDashboardChatTool';
import { useBiDashboardChat } from './chat/useBiDashboardChat';
import { ExportPanel } from './export/ExportPanel';
import { DateRangeBar } from './filter-bar/DateRangeBar';
import { BiCardResults } from './functions/biCardResults';
import { BiDateChoice, biRequestFilters } from './functions/biDateRange';
import { useBiCardEditing } from './functions/useBiCardEditing';
import { useBiCardMoves } from './functions/useBiCardMoves';
import { useBiCardRenderers } from './functions/useBiCardRenderers';
import { useBiChatUndo } from './functions/useBiChatUndo';
import { useBiDashboard } from './functions/useBiDashboard';
import { useBiDashboardActions } from './functions/useBiDashboardActions';
import { useBiLayoutEditing } from './functions/useBiLayoutEditing';
import { useBiSetDefault } from './functions/useBiSetDefault';
import { useBiGhostHost } from './ghost/functions/useBiGhostHost';
import { DashboardPicker } from './picker/DashboardPicker';
import { UndoToast } from './undo/UndoToast';

/** From this window width the chat sits beside the dashboard. */
const kChatBesideFrom = 1024;

/** One dashboard, drawn for the width it has, and everything done to it. */
export const BiDashboardView = memo(
  ({
    dashboardId,
    onBack,
    onOpenCard,
    onSwitch,
    onManage,
    onOpenExports,
    visits,
    choice,
    onChoice,
  }: {
    dashboardId: string;
    /**
     * The viewer's date range for this visit, kept by the page in its route
     * (the URL on the web) so it survives a card's page and a reload.
     */
    choice: BiDateChoice;
    onChoice: (choice: BiDateChoice) => void;
    onBack: () => void;
    /** Shows another dashboard in place of this one (the title picker). */
    onSwitch: (dashboardId: string) => void;
    /** The list of every dashboard. */
    onManage: () => void;
    /** The viewer's PDF exports. */
    onOpenExports: () => void;
    /** Opens a card on its own page. */
    onOpenCard: (cardId: string) => void;
    /**
     * Counts the times the page came back into view: each read the
     * dashboard again, so a change made on a card's page shows here.
     */
    visits?: number;
  }) => {
    const compact = useIsMobileLayout();
    const { width } = useWindowDimensions();
    const chatBeside = width >= kChatBesideFrom;
    const { state, refresh, apply, undo, settleChatTap, toast, dismissToast } =
      useBiDashboard(dashboardId);
    const chat = useBiDashboardChat(dashboardId, settleChatTap);
    useWatchValue(visits, previous => {
      if (previous !== undefined) refresh();
    });
    const { setVisible: setChatVisible } = chat;
    const reveal = useMemo(
      () => (chatBeside ? null : () => setChatVisible(false)),
      [chatBeside, setChatVisible],
    );
    const { host, ghost } = useBiGhostHost(
      dashboardId,
      state.kind === 'ready' ? state.dashboard.revision : null,
      reveal,
    );
    const { onUndo: undoToast, canUndo } = useBiChatUndo({
      dashboardId,
      toast,
      chatBusy: chat.state.busy,
      answer: chat.answer,
      undo,
    });
    // Which layout the viewer is looking at: Reset resets only that one.
    // Null until the grid has measured.
    const [stacked, setStacked] = useState<boolean | null>(null);
    const { arrange, resizeNarrow, reset } = useBiLayoutEditing(
      state,
      apply,
      stacked,
    );
    const saveCard = useBiCardEditing(apply);
    const actions = useBiDashboardActions(apply);
    const [panel, setPanel] = useState<BiDashboardPanel | null>(null);
    const [picking, setPicking] = useState(false);
    const [exporting, setExporting] = useState(false);
    // The sheet belongs to the dashboard it was opened on.
    useEffect(() => setExporting(false), [dashboardId]);
    const { setDefault } = useBiSetDefault();
    const alert = useAlert();
    const dashboard = state.kind === 'ready' ? state.dashboard : null;
    const canEdit = !!dashboard && biCanEdit(dashboard.permission);
    const canManage = dashboard?.permission === BiDashboardPermission.Manage;
    const filters = useMemo(() => biRequestFilters(choice), [choice]);
    const revision = dashboard?.revision ?? 0;
    const { removeCard } = actions;
    const { order, moveCard } = useBiCardMoves(dashboard, apply);
    const addCard = () => setPanel({ kind: 'editor', target: { kind: 'new' } });

    // The filters the cards are viewed under: the viewer's range, else the
    // dashboard's saved ones. The editor previews under them too.
    const viewed = useMemo(
      () => filters ?? dashboard?.filters ?? {},
      [filters, dashboard?.filters],
    );
    const editCard = useCallback(
      (card: BiCardView, loaded: BiQueryResult | null) =>
        setPanel({
          kind: 'editor',
          target: {
            kind: 'edit',
            card,
            revision,
            loaded: loaded ? { result: loaded, filters: viewed } : undefined,
          },
        }),
      [revision, viewed],
    );
    // Refresh reads the dashboard and every card's data again.
    const [refreshTick, setRefreshTick] = useState(0);
    const refreshAll = useCallback(() => {
      refresh();
      setRefreshTick(tick => tick + 1);
    }, [refresh]);
    // What the cards loaded, for the export sheet's preview. Kept across a
    // switch of dashboard: the preview only reads the ids of its own cards.
    const results = useCreation(() => new BiCardResults(), []);
    const { renderCard, renderPlainCard } = useBiCardRenderers({
      dashboard,
      filters,
      refreshTick,
      canEdit,
      order,
      results,
      onOpen: onOpenCard,
      onEdit: editCard,
      onMove: moveCard,
      onRemove: removeCard,
    });

    const shared = dashboard?.visibility === BiDashboardVisibility.Store;
    // A ghost is drawn on the revision it was proposed on only.
    const shownGhost =
      ghost && dashboard && ghost.baseRevision === dashboard.revision
        ? ghost
        : null;
    return (
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          backgroundColor: aiColors.background,
        }}
      >
        <View style={{ flex: 1 }}>
          <BiPageHeader
            title={dashboard?.name ?? '数据看板'}
            onBack={onBack}
            onPressTitle={() => setPicking(true)}
            badge={
              dashboard ? (
                <>
                  {dashboard.isDefault ? (
                    <BiTag text="默认" color={aiColors.primary} />
                  ) : null}
                  {/* A phone keeps the room for the title picker. */}
                  {compact ? null : (
                    <BiTag
                      text={kBiVisibilityTags[dashboard.visibility]}
                      color={shared ? aiColors.green : aiColors.mutedForeground}
                      icon={shared ? Users : Lock}
                    />
                  )}
                </>
              ) : null
            }
            actions={
              <>
                {chatBeside && chat.visible ? null : (
                  <BiButton
                    icon={Sparkles}
                    title={compact ? undefined : '问 AI'}
                    accessibilityLabel="问 AI"
                    onPress={() => setChatVisible(true)}
                  />
                )}
                <DashboardActions
                  title={dashboard?.name ?? '数据看板'}
                  compact={compact}
                  canEdit={canEdit}
                  canManage={canManage}
                  onAdd={addCard}
                  onReset={reset}
                  onHistory={() => setPanel({ kind: 'history' })}
                  onPalette={() => setPanel({ kind: 'palette' })}
                  onRename={() => {
                    actions.clearRenameError();
                    setPanel({ kind: 'rename' });
                  }}
                  onShare={() => setPanel({ kind: 'share' })}
                  onExport={dashboard ? () => setExporting(true) : null}
                  onSetDefault={
                    dashboard && !dashboard.isDefault
                      ? async () => {
                          const outcome = await setDefault(dashboardId);
                          if (outcome?.ok) refresh();
                          else if (outcome)
                            await alert({
                              title: '设为默认',
                              message: outcome.message,
                            });
                        }
                      : null
                  }
                />
              </>
            }
          >
            {dashboard ? (
              <DateRangeBar
                choice={choice}
                saved={dashboard.filters}
                onChange={onChoice}
                trailing={
                  <>
                    {canEdit && choice.kind === 'override' ? (
                      <BiButton
                        icon={Pin}
                        title={compact ? undefined : '设为看板默认'}
                        accessibilityLabel="设为看板默认"
                        onPress={async () => {
                          if (await actions.saveFilters(choice))
                            onChoice({ kind: 'saved' });
                        }}
                      />
                    ) : null}
                    <BiButton
                      icon={RefreshCw}
                      title={compact ? undefined : '刷新'}
                      accessibilityLabel="刷新看板数据"
                      onPress={refreshAll}
                    />
                  </>
                }
              />
            ) : null}
          </BiPageHeader>
          {!dashboard ? (
            <View
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {state.kind === 'loading' ? (
                <ActivityIndicator color={aiColors.primary} />
              ) : state.kind === 'missing' ? (
                <BiRetry
                  label="看板不存在，或你没有查看权限"
                  onRetry={onBack}
                />
              ) : (
                <BiRetry onRetry={refresh} />
              )}
            </View>
          ) : (
            <DashboardContent
              dashboard={dashboard}
              ghost={shownGhost}
              chatBusy={chat.state.busy}
              renderCard={renderCard}
              renderPlainCard={renderPlainCard}
              onArrange={canEdit ? arrange : undefined}
              onResizeNarrow={canEdit ? resizeNarrow : undefined}
              onStackedChange={setStacked}
              onAdd={canEdit ? addCard : null}
              onApplyGhost={changeSetId =>
                chat.answer({
                  action: BiAnswerAction.ApplyChanges,
                  changeSetId,
                })
              }
              onDiscardGhost={changeSetId =>
                chat.answer({
                  action: BiAnswerAction.DiscardChanges,
                  changeSetId,
                })
              }
            />
          )}
          {dashboard ? (
            <>
              <ExportPanel
                visible={exporting}
                dashboardId={dashboardId}
                filters={viewed}
                dashboardPalette={dashboard.palette}
                cards={dashboard.cards}
                order={order}
                results={results}
                onHistory={() => {
                  setExporting(false);
                  onOpenExports();
                }}
                onClose={() => setExporting(false)}
              />
              <DashboardPanels
                dashboard={dashboard}
                filters={viewed}
                panel={panel}
                canManage={canManage}
                renaming={actions.renaming}
                renameError={actions.renameError}
                paletteSaving={actions.paletteSaving}
                onSaveCard={saveCard}
                onPickPalette={async next => {
                  if (await actions.setPalette(next)) setPanel(null);
                }}
                onUndo={undo}
                onRename={async name => {
                  if (await actions.rename(name)) setPanel(null);
                }}
                onShared={refresh}
                onClose={() => setPanel(null)}
              />
            </>
          ) : null}
        </View>
        <DashboardPicker
          visible={picking}
          currentId={dashboardId}
          onClose={() => setPicking(false)}
          onSwitch={onSwitch}
          onManage={onManage}
          onOpenExports={onOpenExports}
          onDefaultChanged={refresh}
        />
        <BiDashboardHostContext.Provider value={host}>
          {chat.visible || chat.opened ? (
            <DashboardChatPanel
              tool={kBiDashboardChatTool}
              side={chatBeside}
              visible={chat.visible}
              conversation={chat.conversation}
              state={chat.state}
              tapSettled$={chat.tapSettled$}
              onClose={() => setChatVisible(false)}
            />
          ) : null}
        </BiDashboardHostContext.Provider>
        {dashboard ? (
          <UndoToast
            toast={toast}
            revision={dashboard.revision}
            canUndo={canUndo}
            onUndo={undoToast}
            onDismiss={dismissToast}
          />
        ) : null}
      </View>
    );
  },
);
